import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import OfferPaymentPlanParamsCard from './OfferPaymentPlanParamsCard';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { loadDocumentScheduleCellParams, updateDocumentScheduleCellParams, loadScheduleTypeCellParamTemplates } from '../actionCreators';
import curry from 'lodash.curry';
import { rulesEngine } from 'utils/rules-engine';
import { collection } from 'utils/immutable';
import { when, nullWhen, propChanged } from 'utils/fn-combinators';
import useEventCallback from 'utils/hooks/useEventCallback';
import { getIsContractModification } from 'selectors/dictionariesSelectors';
import { getOfferType } from 'features/offer/edit/client/form/selectors';
import { emptyString } from 'constants/emptyConst';

const getSelectedScheduleTypeCellParam = (scheduleTypeCellParams, newDoc) =>
  scheduleTypeCellParams.find(x => x.cellParamId === newDoc.cellParamId);
const getCellParamNameForSelectedScheduleTypeCellParam = scheduleTypeCellParams => newDoc => {
  const scheduleTypeCellParam = getSelectedScheduleTypeCellParam(scheduleTypeCellParams, newDoc);
  return scheduleTypeCellParam && scheduleTypeCellParam.cellParamName;
};
const getCellParamDisplayNameForSelectedScheduleTypeCellParam = scheduleTypeCellParams => newDoc => {
  const scheduleTypeCellParam = getSelectedScheduleTypeCellParam(scheduleTypeCellParams, newDoc);
  return scheduleTypeCellParam && scheduleTypeCellParam.cellParamDisplayName;
};
const getControlTypeForSelectedScheduleTypeCellParam = scheduleTypeCellParams => newDoc => {
  const scheduleTypeCellParam = getSelectedScheduleTypeCellParam(scheduleTypeCellParams, newDoc);
  return scheduleTypeCellParam && scheduleTypeCellParam.controlType;
};

const getHeaderBusinessRules = scheduleTypeCellParams => ({
  paramValue: nullWhen(propChanged('cellParamId')),
  cellParamName: when(propChanged('cellParamId'), getCellParamNameForSelectedScheduleTypeCellParam(scheduleTypeCellParams)),
  cellParamDisplayName: when(propChanged('cellParamId'), getCellParamDisplayNameForSelectedScheduleTypeCellParam(scheduleTypeCellParams)),
  controlType: when(propChanged('cellParamId'), getControlTypeForSelectedScheduleTypeCellParam(scheduleTypeCellParams))
});

const initialHeaderValues = {
  cellParamId: null,
  cellParamName: null,
  cellParamDisplayName: null,
  controlType: null,
  paramValue: null
};

export const OfferPaymentPlanParamsContainer = ({
  documentId,
  siteId,
  scheduleCellParams,
  scheduleTypeCellParamTemplates,
  actions,
  isContractModification
}) => {
  const [header, setHeader] = useState(initialHeaderValues);
  const [values, setValues] = useState(scheduleCellParams.values);
  const [isDirty, setDirtyField] = useState(false);
  const [isTemplateChanged, setIsTemplateChanged] = useState(false);
  const [localScheduleTypeCellParamTemplates, setLocalScheduleTypeCellParamTemplates] = useState();
  const [selectedScheduleTypeCellParamTemplate, setSelectedScheduleTypeCellParamTemplate] = useState();
  const [cardExpanded, setExpanded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [isCalendarView, setIsCalendarView] = useState(true);

  const onExpand = (_, expanded) => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    if (!cardExpanded) return;
    actions.loadDocumentScheduleCellParams(documentId, siteId);
    actions.loadScheduleTypeCellParamTemplates(documentId, siteId);
  }, [cardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scheduleCellParams.isLoaded && cardExpanded) {
      actions.loadDocumentScheduleCellParams(documentId, siteId);
      actions.loadScheduleTypeCellParamTemplates(documentId, siteId);
    }
  }, [scheduleCellParams, cardExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scheduleTypeCellParamTemplates.isLoaded) {
      setLocalScheduleTypeCellParamTemplates(scheduleTypeCellParamTemplates.data);
    }
  }, [scheduleTypeCellParamTemplates]);

  useEffect(() => {
    if (localScheduleTypeCellParamTemplates)
      setSelectedScheduleTypeCellParamTemplate(localScheduleTypeCellParamTemplates.filter(x => x.isSelected === true)[0]);
  }, [localScheduleTypeCellParamTemplates]);

  useEffect(() => {
    if (!scheduleCellParams.isLoaded) {
      return;
    }

    setValues(scheduleCellParams.values);
    setDirtyField(false);
  }, [scheduleCellParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isDirty && !isTemplateChanged) return;
    setSelectedOptions(
      values?.map(cellData =>
        scheduleCellParams.columns?.reduce((acc, currentValue) => {
          acc[currentValue.cellParamName] = cellData[currentValue.cellParamName] !== null;
          return acc;
        }, {})
      )
    );
  }, [scheduleCellParams.columns, values, isDirty, isTemplateChanged]);

  const handleHeaderPropertyChange = curry((prop, value) => {
    const { document } = rulesEngine.update(header, getHeaderBusinessRules(scheduleCellParams.columns), {
      [prop]: value
    });
    setHeader(document);
  });

  const handleScheduleTypeCellParamTemplateChange = value => {
    let oldTemplateId = localScheduleTypeCellParamTemplates
      .map(x => {
        return x.isSelected;
      })
      .indexOf(true);
    let newTemplateId = localScheduleTypeCellParamTemplates
      .map(x => {
        return x.scheduleTypeCellParamTemplateId;
      })
      .indexOf(value.scheduleTypeCellParamTemplateId);
    setLocalScheduleTypeCellParamTemplates(
      Object.assign([], {
        ...localScheduleTypeCellParamTemplates,
        [oldTemplateId]: {
          ...localScheduleTypeCellParamTemplates[oldTemplateId],
          isSelected: false
        },
        [newTemplateId]: {
          ...localScheduleTypeCellParamTemplates[newTemplateId],
          isSelected: true
        }
      })
    );
    setValues(prevState => {
      const templateDetails = localScheduleTypeCellParamTemplates[newTemplateId]?.details;
      if (templateDetails && templateDetails.length)
        return prevState.map(item => {
          const index = new Date(item.MonthDate).getMonth();
          if (index < templateDetails.length)
            return Object.fromEntries(
              Object.entries(item).map(([key, _]) => [key, key in templateDetails[index] ? templateDetails[index][key] : item[key]])
            );
          else return item;
        });
      else return prevState;
    });
    handleAllMonthsSelection(false)();
    setDirtyField(true);
    setIsTemplateChanged(true);
  };

  const handleRowPropertyChange = useEventCallback(
    (index, prop, value) => {
      setValues(
        collection.update(
          index,
          x => ({ ...x, [prop]: (typeof value === 'string' && value.trim() === emptyString) || value === undefined ? null : value }),
          values
        )
      );
    },
    [values, isTemplateChanged]
  );

  const handlePropertyParamChange = (rowIdx, paramName, value) => {
    handleRowPropertyChange(rowIdx, paramName, value);
    setDirtyField(true);
    setSelectedOptions(prevState => {
      let newSelectedParams = [...prevState];
      newSelectedParams[rowIdx] = { ...newSelectedParams[rowIdx], [paramName]: Boolean(value) };
      return newSelectedParams;
    });
  };

  const handleRowsPropertyChange = (selectedScheduleCells, cellParamName, value) => {
    setValues(prevState => {
      const newValues = prevState.map(a => ({ ...a }));
      selectedScheduleCells.map(cell => {
        newValues[cell.Index][cellParamName] = value === undefined ? null : value;
      });
      return newValues;
    });
    setDirtyField(true);
  };

  const handleMonthClick = month => () => {
    setValues(prevState => {
      const removeSelection = prevState.some(item => new Date(item.MonthDate).getMonth() === month && item.Selected);
      return prevState.map(item => {
        return {
          ...item,
          Selected: new Date(item.MonthDate).getMonth() === month ? !removeSelection : item.Selected
        };
      });
    });
  };

  const handleYearClick = year => () => {
    setValues(prevState => {
      const removeSelection = prevState.some(item => new Date(item.MonthDate).getFullYear() === year && item.Selected);
      return prevState.map(item => {
        return {
          ...item,
          Selected: new Date(item.MonthDate).getFullYear() === year ? !removeSelection : item.Selected
        };
      });
    });
  };

  const handleAllMonthsSelection = isSelected => () => {
    setValues(prevState =>
      prevState.map(value => {
        return {
          ...value,
          Selected: isSelected
        };
      })
    );
  };

  const onApply = () => {
    if (header == null) return;
    if (header.cellParamName == null) return;
    if (header.paramValue == null) return;

    const newValues = values.map(a => ({ ...a }));

    newValues.forEach(n => {
      if (n['Selected'] === true) {
        n[header.cellParamName] = header.paramValue;
        n['Selected'] = false;
      }
    });

    setHeader(initialHeaderValues);
    resetSelectedOptions(values);
    setValues(newValues);
    setDirtyField(true);
  };

  const resetSelectedOptions = selectedScheduleCells => {
    setSelectedOptions(prevState => {
      const newSelectedOptions = prevState.map(a => ({ ...a }));
      selectedScheduleCells.map(cell => {
        newSelectedOptions[cell.Index] = scheduleCellParams.columns?.reduce((acc, currentValue) => {
          acc[currentValue.cellParamName] = false;
          return acc;
        }, {});
      });
      return newSelectedOptions;
    });
  };

  const onResetSelectedCells = selectedScheduleCells => {
    resetSelectedOptions(selectedScheduleCells);
    setValues(prevState => {
      const newValues = prevState.map(a => ({ ...a }));
      scheduleCellParams.columns.forEach(n => {
        selectedScheduleCells.forEach(x => {
          newValues[x.Index][n.cellParamName] = null;
        });
      });
      return newValues;
    });
  };

  const onReset = () => {
    const newValues = values.map(a => ({ ...a }));
    scheduleCellParams.columns.forEach(n => {
      newValues.forEach(x => {
        x[n.cellParamName] = null;
        x['Selected'] = false;
      });
    });

    setHeader(initialHeaderValues);
    setValues(newValues);
    resetSelectedOptions(values);
    setDirtyField(true);
  };

  const handleSave = scheduleCellParams => {
    actions.updateDocumentScheduleCellParams(
      documentId,
      siteId,
      scheduleCellParams,
      selectedScheduleTypeCellParamTemplate ? selectedScheduleTypeCellParamTemplate.scheduleTypeCellParamTemplateId : null
    );
    setIsTemplateChanged(false);
  };

  const handleCalendarViewChange = () => setIsCalendarView(prevState => !prevState);

  return (
    <OfferPaymentPlanParamsCard
      scheduleCellParamHeader={header}
      scheduleCellParamColumns={scheduleCellParams.columns}
      scheduleCellParamValues={values}
      handleScheduleTypeCellParamTemplateChange={handleScheduleTypeCellParamTemplateChange}
      scheduleTypeCellParamTemplates={localScheduleTypeCellParamTemplates}
      selectedScheduleTypeCellParamTemplate={selectedScheduleTypeCellParamTemplate}
      handleHeaderPropertyChange={handleHeaderPropertyChange}
      handleRowPropertyChange={handleRowPropertyChange}
      handlePropertyParamChange={handlePropertyParamChange}
      handleRowsPropertyChange={handleRowsPropertyChange}
      onSave={handleSave}
      onApply={onApply}
      onReset={onReset}
      onExpand={onExpand}
      cardExpanded={cardExpanded}
      saving={scheduleCellParams.saving}
      isDirty={isDirty}
      isTemplateChanged={isTemplateChanged}
      isContractModification={isContractModification}
      handleMonthClick={handleMonthClick}
      handleYearClick={handleYearClick}
      handleAllMonthsSelection={handleAllMonthsSelection}
      selectedOptions={selectedOptions}
      setSelectedOptions={setSelectedOptions}
      onResetSelectedCells={onResetSelectedCells}
      isCalendarView={isCalendarView}
      handleCalendarViewChange={handleCalendarViewChange}
    />
  );
};

OfferPaymentPlanParamsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  scheduleCellParams: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  scheduleTypeCellParamTemplates: PropTypes.object,
  isContractModification: PropTypes.bool.isRequired
};

export function mapStateToProps(state) {
  const offerTypeId = getOfferType(state);
  const { scheduleCellParams } = state.currentOffer.paymentPlanData;
  const { scheduleTypeCellParamTemplates } = state.currentOffer.paymentPlanData;
  return {
    scheduleCellParams,
    scheduleTypeCellParamTemplates,
    isContractModification: getIsContractModification(offerTypeId)
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentScheduleCellParams,
        updateDocumentScheduleCellParams,
        loadScheduleTypeCellParamTemplates
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(OfferPaymentPlanParamsContainer);
